import React, { useMemo } from 'react';
import type { BaseEntity, PermissionName } from 'c-sdk';
import { useBoolean } from 'react-hanger';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import CreateEntityDialog from './CreateEntityDialog';
import type { EntityDialogProps } from '../EntityDialog/EntityDialog';
import Button from '../../Forms/Button';

const CreateEntityButton = <Entity extends BaseEntity>({
    buttonLabel,
    show,
    onClose,
    permissions,
    ...props
}: Omit<EntityDialogProps<Entity>, 'show'> & {
    buttonLabel: React.ReactNode;
    show?: boolean;
    permissions?: PermissionName[];
}) => {
    const { hasAll } = useUserPermissions();
    const addEntity = useBoolean(false);

    const hasPerms = useMemo(() => hasAll(permissions), [hasAll, permissions]);

    if (!hasPerms) return null;

    return (
        <>
            <IfHasAllPermissions permissions={permissions}>
                <Button
                    size="small"
                    sx={{
                        minHeight: 26,
                        minWidth: 0,
                        px: 1,
                        py: 0.3,
                        fontSize: '0.72rem',
                        lineHeight: 1.1,
                    }}
                    onClick={addEntity.toggle}
                    id={`new-${props.entityName as string}`}
                >
                    {buttonLabel}
                </Button>
            </IfHasAllPermissions>
            <CreateEntityDialog<Entity>
                {...props}
                onClose={onClose ?? addEntity.setFalse}
                show={show ?? addEntity.value}
            />
        </>
    );
};

export default CreateEntityButton;
